import { Cache, FileLoader } from "three";

// Enable three.js's in-memory cache so FileLoader stores/reuses ArrayBuffers
// keyed by URL. TalkingHead's showAvatar() loads the GLB through a fresh
// GLTFLoader -> FileLoader, so warming this cache with the exact same URL
// beforehand lets showAvatar skip the network download (only the parse remains).
Cache.enabled = true;

const inflight = new Map<string, Promise<void>>();

/**
 * Prefetch an avatar GLB into three.js's Cache so a later showAvatar() call
 * with the same URL resolves without hitting the network.
 *
 * The URL must match byte-for-byte the one showAvatar() uses
 * (see AnimatedCharacter.vue: `${publicPath}avatars/${gender}-merged.glb`),
 * otherwise the cache won't hit.
 */
export const preloadAvatarGlb = async (url: string) => {
  if (Cache.get(url)) {
    return;
  } else if (inflight.has(url)) {
    return inflight.get(url)!;
  }

  const promise = new Promise<void>((resolve, reject) => {
    new FileLoader().setResponseType("arraybuffer").load(
      url,
      () => {
        inflight.delete(url); // sits in Cache under this exact URL now
        resolve();
      },
      ev => {
        /* empty */
      },
      err => {
        inflight.delete(url);
        reject(err);
      } // on error, let showAvatar retry the fetch
    );
  });

  inflight.set(url, promise);

  return promise;
};
